import React, { useState } from 'react';
import { AlertTriangle, Info, X, Grid, Activity } from 'lucide-react';

const Heatmap = ({ heatmapData, loading = false, apiClient }) => {
  const [selectedCell, setSelectedCell] = useState(null);
  const [cellDetails, setCellDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const handleCellClick = async (unit, issueCategory, count) => {
    if (count === 0) return;

    setSelectedCell({ unit, issueCategory, count });
    setLoadingDetails(true);
    setCellDetails(null);

    try {
      const response = await apiClient.get('/analysis/heatmap/details', {
        params: {
          unit_name: unit,
          issue_category: issueCategory,
          maintenance_type: heatmapData?.maintenance_type || 'unplanned'
        }
      });
      setCellDetails(response.data);
    } catch (error) {
      console.error('Error fetching cell details:', error);
      setCellDetails({ error: error.message || 'Failed to load details' });
    } finally {
      setLoadingDetails(false);
    }
  };
  if (loading || !heatmapData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading heatmap data...</p>
        </div>
      </div>
    );
  }

  if (heatmapData.error) {
    return (
      <div className="flex items-center justify-center h-96 text-gray-400">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Error loading heatmap data</p>
          <p className="text-xs text-gray-500 mt-1">
            {heatmapData.error}
          </p>
        </div>
      </div>
    );
  }

  const { heatmap_data, statistics, hotspots = [], insights = [] } = heatmapData;

  // Robust check for heatmap_data to handle both new nested format and potential legacy cache
  const data = heatmap_data || (heatmapData.matrix ? heatmapData : null);

  if (!data || !data.matrix) {
    return (
      <div className="flex items-center justify-center h-96 text-gray-400">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Invalid heatmap data format</p>
          <button
            onClick={() => window.location.reload()}
            className="text-xs text-blue-500 mt-2 hover:underline"
          >
            Clear cache & refresh
          </button>
        </div>
      </div>
    );
  }

  const { matrix, x_labels = [], y_labels = [], max_value = 0 } = data;

  // Hide preventive-maintenance category in heatmap (also covers cached legacy payloads).
  const hiddenCategories = new Set([
    'Preventive Maintenance',
    'Preventive Maint.',
    'Preventative Maintenance',
    'Preventative Maint.',
  ]);
  const visibleColumnIndexes = x_labels
    .map((label, idx) => ({ label, idx }))
    .filter(({ label }) => !hiddenCategories.has(label))
    .map(({ idx }) => idx);
  const visibleLabels = visibleColumnIndexes.map((idx) => x_labels[idx]);
  const visibleMatrix = matrix.map((row) => visibleColumnIndexes.map((idx) => row[idx] || 0));
  const visibleMaxValue = visibleMatrix.length
    ? Math.max(...visibleMatrix.flat().map((v) => Number(v) || 0))
    : max_value;

  // Color intensity function with improved scaling
  const getColorIntensity = (value) => {
    // Handle 0 or falsy values as white
    if (!value || value === 0 || value === '0') return 'bg-white text-gray-400';
    const intensity = Math.min(value / (visibleMaxValue || 1), 1);

    if (intensity <= 0.1) return 'bg-yellow-100 text-yellow-800';
    if (intensity <= 0.2) return 'bg-yellow-200 text-yellow-900';
    if (intensity <= 0.3) return 'bg-orange-200 text-orange-900';
    if (intensity <= 0.4) return 'bg-orange-300 text-orange-900';
    if (intensity <= 0.5) return 'bg-red-200 text-red-900';
    if (intensity <= 0.6) return 'bg-red-300 text-red-900';
    if (intensity <= 0.7) return 'bg-red-400 text-white';
    if (intensity <= 0.8) return 'bg-red-500 text-white';
    if (intensity <= 0.9) return 'bg-red-600 text-white font-semibold';
    return 'bg-red-700 text-white font-bold';
  };

  // Shorten long category names for better display
  const shortenLabel = (label) => {
    const maxLength = 12;
    // Common abbreviations for long terms
    const abbreviations = {
      'Preventive Maintenance': 'Preventive Maint.',
      'Tire/Wheel': 'Tire/Wheel',
      'Structural': 'Structural',
      'Electrical': 'Electrical',
    };

    // Return abbreviation if available
    if (abbreviations[label]) {
      return abbreviations[label];
    }

    // Truncate if too long
    if (label.length > maxLength) {
      return label.substring(0, maxLength - 1) + '…';
    }

    return label;
  };



  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm flex items-center justify-between group hover:border-blue-200 transition-colors">
          <div>
            <div className="text-sm text-gray-500 font-medium mb-1">Total Combinations</div>
            <div className="text-2xl font-bold text-gray-900">{statistics.total_combinations}</div>
          </div>
          <div className="p-3 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
            <Grid className="w-6 h-6 text-blue-600" />
          </div>
        </div>

        <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm flex items-center justify-between group hover:border-green-200 transition-colors">
          <div>
            <div className="text-sm text-gray-500 font-medium mb-1">Active Patterns</div>
            <div className="text-2xl font-bold text-gray-900">{statistics.active_combinations}</div>
          </div>
          <div className="p-3 bg-green-50 rounded-lg group-hover:bg-green-100 transition-colors">
            <Activity className="w-6 h-6 text-green-600" />
          </div>
        </div>

        <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm flex items-center justify-between group hover:border-red-200 transition-colors">
          <div>
            <div className="text-sm text-gray-500 font-medium mb-1">Max Events</div>
            <div className="text-2xl font-bold text-gray-900">{statistics.max_occurrences}</div>
          </div>
          <div className="p-3 bg-red-50 rounded-lg group-hover:bg-red-100 transition-colors">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
        </div>
      </div>

      {/* Heatmap with right-side summary cards */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
        {/* Heatmap Grid with Legend - scroll horizontally on small screens */}
        <div className="xl:col-span-8 bg-white border border-gray-200 rounded-lg p-4 sm:p-6 overflow-hidden">
          <div className="overflow-x-auto overflow-y-visible -mx-2 px-2 sm:mx-0 sm:px-0">
            <div className="flex items-start gap-4 sm:gap-6 min-w-0" style={{ minWidth: 'min-content' }}>
              {/* Legend - Left side of heatmap; responsive text */}
              <div className="flex-shrink-0 flex flex-col items-center space-y-2 pt-6 sm:pt-8">
                <span className="text-[10px] sm:text-sm font-semibold text-gray-700 mb-2">Intensity</span>
                <div className="flex flex-col items-center space-y-1">
                  <span className="text-[10px] sm:text-sm font-medium text-gray-700">{visibleMaxValue}</span>
                  <div className="flex flex-col h-64 w-12 border border-gray-300 rounded overflow-hidden shadow-sm">
                    <div className="flex-1 bg-red-700"></div>
                    <div className="flex-1 bg-red-600"></div>
                    <div className="flex-1 bg-red-500"></div>
                    <div className="flex-1 bg-red-400"></div>
                    <div className="flex-1 bg-red-300"></div>
                    <div className="flex-1 bg-orange-300"></div>
                    <div className="flex-1 bg-orange-200"></div>
                    <div className="flex-1 bg-yellow-200"></div>
                    <div className="flex-1 bg-yellow-100"></div>
                    <div className="flex-1 bg-white"></div>
                  </div>
                  <span className="text-[10px] sm:text-sm font-medium text-gray-700">0</span>
                </div>
              </div>

              {/* Heatmap - fixed min width so cells and numbers stay visible */}
              <div className="flex-1 min-w-[min(100%,800px)]" style={{ minWidth: 200 + visibleLabels.length * 56 }}>
                {/* Column Headers � responsive: smaller text and cells on very small screens */}
                <div className="grid gap-0.5 sm:gap-1 mb-8 sm:mb-10" style={{ gridTemplateColumns: `minmax(60px,80px) repeat(${visibleLabels.length}, minmax(40px,52px))` }}>
                  <div className="text-[10px] sm:text-sm font-medium text-gray-700 p-1 sm:p-2"></div>
                  {visibleLabels.map((label, index) => (
                    <div
                      key={index}
                      className="text-[10px] sm:text-xs font-bold text-gray-700 p-1 sm:p-2 text-center transform -rotate-45 origin-bottom translate-x-0.5 sm:translate-x-1 -translate-y-2"
                      style={{ height: '80px', display: 'flex', alignItems: 'end', justifyContent: 'center' }}
                      title={label}
                    >
                      <span className="whitespace-nowrap inline-block leading-none">{shortenLabel(label)}</span>
                    </div>
                  ))}
                </div>

                {/* Heatmap Rows */}
                {visibleMatrix.map((row, rowIndex) => (
                  <div key={rowIndex} className="grid gap-0.5 sm:gap-1 mb-0.5 sm:mb-1" style={{ gridTemplateColumns: `minmax(60px,80px) repeat(${visibleLabels.length}, minmax(40px,52px))` }}>
                    <div className="text-[10px] sm:text-xs font-medium text-gray-700 p-1 sm:p-2 flex items-center min-w-0">
                      <span className="line-clamp-2 truncate" title={y_labels[rowIndex]}>
                        {y_labels[rowIndex]}
                      </span>
                    </div>
                    {row.map((value, colIndex) => {
                      const isZero = !value || value === 0 || value === '0';
                      return (
                        <div
                          key={colIndex}
                          onClick={() => handleCellClick(y_labels[rowIndex], visibleLabels[colIndex], value)}
                          className={`h-9 sm:h-12 min-w-[40px] sm:min-w-[52px] flex items-center justify-center text-[10px] sm:text-xs font-medium border border-gray-200 rounded transition-all hover:scale-110 hover:z-10 hover:shadow-lg ${value > 0 ? 'cursor-pointer' : 'cursor-default'} ${getColorIntensity(value)}`}
                          style={isZero ? { backgroundColor: '#ffffff' } : {}}
                          title={value > 0 ? `Click to view details: ${visibleLabels[colIndex]} on ${y_labels[rowIndex]} - ${value} events` : `${visibleLabels[colIndex]} on ${y_labels[rowIndex]}: No events`}
                        >
                          {value > 0 ? value : ''}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right side: insights + top combinations */}
        <div className="xl:col-span-4 space-y-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <Info className="h-5 w-5 mr-2 text-blue-500" />
              Key Insights
            </h4>
            <div className="space-y-3">
              {insights.map((insight, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <p className="text-sm text-gray-700 leading-relaxed">{insight}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2 text-orange-500" />
              Top Event-Unit Combinations
            </h4>
            <div className="space-y-3">
              {hotspots.slice(0, 5).map((hotspot, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-800">
                      {hotspot.issue}
                    </div>
                    <div className="text-xs text-gray-600">
                      Unit: {hotspot.unit}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-red-600">{hotspot.count}</div>
                    <div className="text-xs text-gray-500">{hotspot.percentage}%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Detail Panel (inline, near heatmap) */}
      {selectedCell && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          {/* Panel Header */}
          <div className="bg-gradient-to-r from-red-500 to-pink-600 p-6 text-white flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold">
                {selectedCell.issueCategory} - {selectedCell.unit}
              </h3>
              <p className="text-red-100 text-sm mt-1">
                {selectedCell.count} occurrence{selectedCell.count !== 1 ? 's' : ''}
              </p>
            </div>
            <button
              onClick={() => setSelectedCell(null)}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Panel Content */}
          <div className="p-6">
            {loadingDetails ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading details...</p>
                </div>
              </div>
            ) : cellDetails?.error ? (
              <div className="text-center text-red-500 py-8">
                <AlertTriangle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>{cellDetails.error}</p>
              </div>
            ) : cellDetails?.records ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-gray-800">{cellDetails.total_records}</div>
                    <div className="text-sm text-gray-600">Total Records</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-gray-800">{cellDetails.unique_issues}</div>
                    <div className="text-sm text-gray-600">Unique Events</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-gray-800">{cellDetails.date_range || 'N/A'}</div>
                    <div className="text-sm text-gray-600">Date Range</div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Maintenance Records:</h4>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {cellDetails.records.map((record, index) => (
                      <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-800 mb-1">
                              {record.issue_description || record.description || 'N/A'}
                            </p>
                            {record.date && (
                              <p className="text-xs text-gray-600">
                                Date: {new Date(record.date).toLocaleDateString()}
                              </p>
                            )}
                            {record.downtime_hours !== undefined && record.downtime_hours !== null && (
                              <p className="text-xs text-gray-500 mt-1">
                                Downtime: {Number(record.downtime_hours).toFixed(1)}h
                              </p>
                            )}
                            {!record.downtime_hours && record.issue_code && (
                              <p className="text-xs text-gray-500 mt-1">
                                Code: {record.issue_code}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <p>No details available</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Heatmap;



